import { useState, useRef, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { PlatformPicker } from "./PlatformPicker";
import { toast } from "sonner";
import { scanClip } from "../lib/clipScan";
import { uploadVideoChunked } from "../lib/uploadVideo";
import { api, fileUrl, formatApiError } from "../lib/api";
import { GAME_MODES, CHEAT_TYPES, SCAN_PRESETS } from "../lib/constants";
import { ClipPlayer } from "./ClipPlayer";
import { Sparkles, ScanSearch, Clock, Copy, Loader2, Wand2, ChevronDown, ChevronUp, Check, Zap, FlaskConical, Smartphone, FilePlus, Target, ShieldAlert, History, RotateCcw } from "lucide-react";

const LEAD_IN_SECONDS = 3;

const ShotBadges = ({ r }) => (
  <span className="flex shrink-0 items-center gap-1">
    {r.green && <span title="Perfect / green release" className="inline-flex items-center gap-0.5 rounded bg-[#00FF87]/15 px-1 py-0.5 text-[9px] font-bold uppercase text-[#00FF87]"><Check size={9} /> Green</span>}
    {r.contested && <span title="Contested at release" className="inline-flex items-center gap-0.5 rounded bg-[#FF2A55]/15 px-1 py-0.5 text-[9px] font-bold uppercase text-[#FF6B8A]"><ShieldAlert size={9} /> Contested</span>}
    {r.highlight && <span title="Matches your search" className="inline-flex items-center gap-0.5 rounded bg-[#FFB800]/20 px-1 py-0.5 text-[9px] font-bold uppercase text-[#FFB800]"><Target size={9} /> Match</span>}
  </span>
);

export const ClipScannerPanel = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [prompt, setPrompt] = useState("");
  const [player, setPlayer] = useState("");
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState(null);
  const [engine, setEngine] = useState(null);
  const [note, setNote] = useState(null);
  const [copied, setCopied] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showCase, setShowCase] = useState(false);
  const [creating, setCreating] = useState(false);
  const [caseForm, setCaseForm] = useState({ gamertag: "", platform: "", game_mode: "", cheat_type: "", description: "" });
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const videoFileRef = useRef(null);
  const playerRef = useRef(null);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/clip-scan/history");
      setHistory(data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { if (open) loadHistory(); }, [open]);

  const reopen = (h) => {
    setUrl(h.url || "");
    setPrompt(h.prompt || "");
    setPlayer(h.player || "");
    setResults(h.results || []);
    setEngine(h.engine || "gemini");
    setNote(null);
    toast.success("Re-opened a past scan — no re-upload needed.");
  };

  const createCase = async () => {
    const f = caseForm;
    if (!url.trim()) { toast.error("Scan or upload a clip first."); return; }
    if (!f.gamertag || !f.platform || !f.game_mode || !f.cheat_type || !f.description) {
      toast.error("Fill out every field to create the case.");
      return;
    }
    const gemini = engine === "gemini" ? (results || []) : [];
    const greens = gemini.filter((r) => r.green);
    const top = [...(greens.length ? greens : gemini)].sort((a, b) => (b.conf || 0) - (a.conf || 0))[0];
    const ev = { url: url.trim(), timestamp: top?.ts || "", note: top?.label || "" };
    setCreating(true);
    try {
      const { data } = await api.post("/reports", {
        gamertag: f.gamertag.trim(), platform: f.platform, game_mode: f.game_mode,
        cheat_type: f.cheat_type, description: f.description.trim(),
        video_links: [url.trim()], video_evidence: [ev], report_type: "mod",
      });
      toast.success(`Case created for ${data.gamertag} — it's now in the review queue below.`);
      setShowCase(false);
      setCaseForm({ gamertag: "", platform: "", game_mode: "", cheat_type: "", description: "" });
      onCreated && onCreated();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not create the case.");
    } finally {
      setCreating(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (videoFileRef.current) videoFileRef.current.value = "";
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) {
      toast.error("That clip is over 200MB. Trim it or upload a lower-resolution version.");
      return;
    }
    setUploading(true);
    setProgress(0);
    setResults(null);
    try {
      const uploaded = await uploadVideoChunked(file, setProgress);
      setUrl(uploaded);
      toast.success("Clip uploaded! Hit Scan to analyze it.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed. Try a smaller clip.");
    } finally {
      setUploading(false);
    }
  };

  const runScan = async () => {
    if (!url.trim()) {
      toast.error("Paste a clip link to scan.");
      return;
    }
    setResults(null);
    setEngine(null);
    setNote(null);
    setScanning(true);
    try {
      const data = await scanClip({ url: url.trim(), prompt: prompt.trim(), player: player.trim() });
      setResults(data.results || []);
      setEngine(data.engine);
      setNote(data.note || null);
      if (data.engine === "gemini") loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan failed. Try again.");
    } finally {
      setScanning(false);
    }
  };

  const seek = (r) => {
    if (playerRef.current) playerRef.current.seekTo(Math.max(0, r.sec - LEAD_IN_SECONDS));
  };

  const pickPreset = (p) => setPrompt(p === "All jump shots" ? "" : p);

  const copyTs = (r) => {
    navigator.clipboard?.writeText(r.ts);
    setCopied(r.ts);
    toast.success(`Copied timestamp ${r.ts}`);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="mt-8 rounded-2xl border border-[#00F0FF]/25 bg-gradient-to-b from-[#0B1B24] to-[#0F1424] p-5" data-testid="clip-scanner-panel">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center justify-between gap-3 text-left" data-testid="clip-scanner-toggle">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#00F0FF]/15 text-[#00F0FF]"><Sparkles size={18} /></span>
          <div>
            <h3 className="font-heading text-lg font-bold text-white">AI Clip Scanner</h3>
            <p className="text-xs text-[#94A3B8]">Auto-find the exact moments in a clip by describing them. Mods only.</p>
          </div>
        </div>
        {open ? <ChevronUp size={18} className="text-[#64748B]" /> : <ChevronDown size={18} className="text-[#64748B]" />}
      </button>

      {open && (
        <div className="mt-5">
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Clip link</label>
              <Input
                data-testid="scanner-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="Paste a Streamable / Medal / Twitch / YouTube / direct MP4 link"
                className="mt-1.5 border-[#1E293F] bg-[#0F1424] text-white placeholder:text-[#64748B]"
              />
              <p className="mt-1 text-[11px] leading-snug text-[#64748B]">Paste a YouTube, Streamable, Medal, Twitch, Google Drive or direct MP4 link — or scan a clip uploaded to a case. The AI watches the actual video and timestamps the moments you describe.</p>

              <div className="mt-3 rounded-xl border border-dashed border-[#00FF87]/30 bg-[#00FF87]/[0.04] p-3" data-testid="scanner-upload-box">
                <input ref={videoFileRef} type="file" accept="video/*" hidden onChange={handleUpload} data-testid="scanner-video-input" />
                <button type="button" onClick={() => videoFileRef.current?.click()} disabled={uploading}
                  className="flex w-full items-center gap-2.5 text-left" data-testid="scanner-upload-btn">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#00FF87]/15 text-[#00FF87]">
                    {uploading ? <Loader2 size={17} className="animate-spin" /> : <Smartphone size={17} />}
                  </span>
                  <span className="flex-1">
                    <span className="block text-sm font-semibold text-white">{uploading ? `Uploading… ${progress}%` : "Or upload a clip from your phone or PC"}</span>
                    <span className="block text-xs text-[#94A3B8]">No need to submit a full report — drop an MP4/MOV/WEBM straight into the scanner (max 200MB).</span>
                  </span>
                </button>
                {uploading && (
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[#0F1424]" data-testid="scanner-upload-progress">
                    <div className="h-full rounded-full bg-[#00FF87] transition-all" style={{ width: `${progress}%` }} />
                  </div>
                )}
                {url.trim().startsWith("/api/files/") && !uploading && (
                  <p className="mt-2 flex items-center gap-1.5 text-[11px] font-semibold text-[#00FF87]" data-testid="scanner-uploaded-note"><Check size={12} /> Clip uploaded — press Scan to analyze it.</p>
                )}
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Whose shots? <span className="normal-case text-[#475569]">(gamertag — optional, helps the AI lock onto the right player)</span></label>
              <Input
                data-testid="scanner-player"
                value={player}
                onChange={(e) => setPlayer(e.target.value)}
                placeholder="e.g. GreenLightBandit"
                className="mt-1.5 border-[#1E293F] bg-[#0F1424] text-white placeholder:text-[#64748B]"
              />
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Highlight specific shots <span className="normal-case text-[#475569]">(leave empty to log every shot)</span></label>
              <div className="mt-1.5 flex gap-2">
                <div className="relative flex-1">
                  <Wand2 size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#00F0FF]" />
                  <Input
                    data-testid="scanner-prompt"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder='empty = every shot · or e.g. "half court or beyond", "perfect greens"'
                    className="border-[#1E293F] bg-[#0F1424] pl-9 text-white placeholder:text-[#64748B]"
                  />
                </div>
                <Button data-testid="scanner-run-btn" onClick={runScan} disabled={scanning}
                  className="shrink-0 bg-[#00F0FF] font-bold text-[#04222A] hover:bg-[#00F0FF]/90">
                  {scanning ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <ScanSearch size={15} className="mr-1.5" />} Scan
                </Button>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {SCAN_PRESETS.map((q) => (
                  <button key={q} data-testid={`scanner-chip-${q.replace(/[^a-z0-9]+/gi, "-")}`} onClick={() => pickPreset(q)}
                    className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${(prompt === q || (q === "All jump shots" && !prompt)) ? "border-[#00F0FF] bg-[#00F0FF]/15 text-[#00F0FF]" : "border-[#1E293F] bg-[#0F1424] text-[#94A3B8] hover:border-[#00F0FF]/40 hover:text-[#00F0FF]"}`}>
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {history.length > 0 && (
            <div className="mt-4 rounded-xl border border-[#1E293F] bg-[#0B1120] p-3" data-testid="scanner-history">
              <button onClick={() => setShowHistory(!showHistory)} className="flex w-full items-center gap-2 text-left text-xs font-bold uppercase tracking-wider text-[#94A3B8]" data-testid="scanner-history-toggle">
                <History size={14} /> Recent scans ({history.length})
                <span className="ml-auto normal-case text-[#64748B]">{showHistory ? "hide" : "show"}</span>
              </button>
              {showHistory && (
                <div className="mt-2 max-h-56 space-y-1.5 overflow-y-auto pr-1">
                  {history.map((h, i) => (
                    <div key={i} className="flex items-center gap-2 rounded border border-[#1E293F] bg-[#0F1424] p-2" data-testid={`scanner-history-row-${i}`}>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs text-white">{(h.url || "").startsWith("/api/files/") ? "Uploaded clip" : h.url}</p>
                        <p className="text-[10px] text-[#64748B]">{h.count} shots{h.player ? ` · ${h.player}` : ""}{h.prompt ? ` · "${h.prompt}"` : ""}</p>
                      </div>
                      <Button size="sm" variant="outline" data-testid={`scanner-history-reopen-${i}`} onClick={() => reopen(h)}
                        className="h-7 shrink-0 border-[#00F0FF]/40 bg-[#00F0FF]/10 px-2 text-[11px] font-semibold text-[#00F0FF] hover:bg-[#00F0FF]/20 hover:text-[#00F0FF]">
                        <RotateCcw size={12} className="mr-1" /> Re-open
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {url.trim() && (
            <div className="mt-4">
              <ClipPlayer ref={playerRef} url={url.trim().startsWith("/api/files/") ? fileUrl(url.trim()) : url.trim()} testid="scanner-player" />
            </div>
          )}

          {scanning && (
            <div className="mt-4 flex items-center gap-2 rounded-lg border border-[#1E293F] bg-[#0F1424] p-4 text-sm text-[#00F0FF]" data-testid="scanner-progress">
              <Loader2 size={15} className="animate-spin" /> Analyzing the clip… this can take up to a minute for longer videos.
            </div>
          )}

          {results && !scanning && (
            <div className="mt-4" data-testid="scanner-results">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-bold text-white">{results.length} moment{results.length === 1 ? "" : "s"} found</h4>
                  {engine === "gemini" ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#00FF87]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#00FF87]"><Zap size={10} /> Live AI</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#FFB800]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#FFB800]"><FlaskConical size={10} /> Sample</span>
                  )}
                </div>
                <span className="text-[11px] text-[#64748B]">click a time to jump the player</span>
              </div>
              {note && <p className="mt-2 rounded-lg border border-[#FFB800]/25 bg-[#FFB800]/[0.06] px-3 py-2 text-[11px] leading-snug text-[#FCD34D]" data-testid="scanner-note">{note}</p>}
              <div className="mt-3 space-y-2">
                {results.map((r, i) => (
                  <div key={i} data-testid={`scanner-result-${i}`} className={`flex items-center gap-3 rounded-lg border p-3 ${engine === "demo" ? "border-dashed border-[#334155] bg-[#0F1424]/60 opacity-70" : "border-[#1E293F] bg-[#0F1424]"}`}>
                    {engine === "demo" ? (
                      <span title="Example only — this clip couldn't be scanned" className="flex shrink-0 items-center gap-1.5 rounded bg-[#334155]/40 px-2 py-1 font-mono text-sm font-bold text-[#64748B]">
                        <Clock size={13} /> {r.ts}
                      </span>
                    ) : (
                      <button onClick={() => seek(r)} data-testid={`scanner-seek-${i}`}
                        className="flex shrink-0 items-center gap-1.5 rounded bg-[#00F0FF]/15 px-2 py-1 font-mono text-sm font-bold text-[#00F0FF] transition-colors hover:bg-[#00F0FF]/30">
                        <Clock size={13} /> {r.ts}
                      </button>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className={`truncate text-sm font-semibold ${engine === "demo" ? "text-[#64748B]" : "text-white"}`}>{r.label}{engine === "demo" && <span className="ml-1.5 rounded bg-[#334155]/50 px-1 text-[9px] font-bold uppercase text-[#94A3B8]">example</span>}</p>
                        {engine !== "demo" && <ShotBadges r={r} />}
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <div className="h-1.5 w-24 overflow-hidden rounded-full bg-[#141B2D]">
                          <div className={`h-full rounded-full ${engine === "demo" ? "bg-[#475569]" : "bg-[#00FF87]"}`} style={{ width: `${r.conf}%` }} />
                        </div>
                        <span className={`text-[11px] font-mono ${engine === "demo" ? "text-[#64748B]" : "text-[#00FF87]"}`}>{r.conf}%</span>
                      </div>
                    </div>
                    {engine !== "demo" && (
                      <Button size="sm" variant="outline" onClick={() => copyTs(r)} data-testid={`scanner-copy-${i}`}
                        className="shrink-0 border-[#1E293F] bg-[#141B2D] text-[#94A3B8] hover:bg-[#1A233A] hover:text-white">
                        {copied === r.ts ? <Check size={14} className="text-[#00FF87]" /> : <Copy size={14} />}
                      </Button>
                    )}
                  </div>
                ))}
                {results.length === 0 && (
                  <p className="rounded-lg border border-dashed border-[#1E293F] bg-[#0F1424] py-6 text-center text-sm text-[#64748B]" data-testid="scanner-empty">No matching moments. Try a broader prompt.</p>
                )}
              </div>
              <p className="mt-3 text-[11px] leading-snug text-[#64748B]">
                💡 Copy a timestamp and drop it into a case's <span className="font-semibold text-[#94A3B8]">Video Proof → "Jump to"</span> field — or use the per-clip scanner right inside a case to tag it in one click.
              </p>
            </div>
          )}

          {url.trim() && !scanning && (
            <div className="mt-4 rounded-xl border border-[#F97316]/25 bg-[#F97316]/[0.05] p-3" data-testid="scanner-case-box">
              <button onClick={() => setShowCase(!showCase)} className="flex w-full items-center gap-2 text-left text-sm font-bold text-[#F97316]" data-testid="scanner-create-case-toggle">
                <FilePlus size={16} /> Create a case from this clip
                <span className="ml-auto text-xs font-normal text-[#64748B]">{showCase ? "hide" : "open"}</span>
              </button>
              {showCase && (
                <div className="mt-3 space-y-3">
                  <p className="text-xs leading-relaxed text-[#94A3B8]">
                    Turns this clip into a new report in the queue below — you can then <span className="font-semibold text-white">confirm it as a cheater or reject it</span> like any other case.
                    {results && results.length > 0 && engine === "gemini" && <> The AI's top moment ({results[0].ts}) will be pinned as the jump-to timestamp.</>}
                  </p>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Cheater gamertag</Label>
                    <Input data-testid="scanner-case-gamertag" value={caseForm.gamertag}
                      onChange={(e) => setCaseForm({ ...caseForm, gamertag: e.target.value })}
                      placeholder="e.g. GreenLightBandit" className="mt-1.5 border-[#1E293F] bg-[#0F1424] text-white placeholder:text-[#64748B]" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Platform</Label>
                    <PlatformPicker value={caseForm.platform} onChange={(v) => setCaseForm({ ...caseForm, platform: v })} />
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Game mode</Label>
                      <Select value={caseForm.game_mode} onValueChange={(v) => setCaseForm({ ...caseForm, game_mode: v })}>
                        <SelectTrigger data-testid="scanner-case-mode" className="mt-1.5 border-[#1E293F] bg-[#0F1424] text-white"><SelectValue placeholder="Select mode" /></SelectTrigger>
                        <SelectContent className="border-[#1E293F] bg-[#141B2D] text-white">
                          {GAME_MODES.map((m) => <SelectItem key={m} value={m} className="focus:bg-[#1A233A] focus:text-white">{m}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Cheat type</Label>
                      <Select value={caseForm.cheat_type} onValueChange={(v) => setCaseForm({ ...caseForm, cheat_type: v })}>
                        <SelectTrigger data-testid="scanner-case-cheat" className="mt-1.5 border-[#1E293F] bg-[#0F1424] text-white"><SelectValue placeholder="Select cheat" /></SelectTrigger>
                        <SelectContent className="border-[#1E293F] bg-[#141B2D] text-white">
                          {CHEAT_TYPES.map((c) => <SelectItem key={c} value={c} className="focus:bg-[#1A233A] focus:text-white">{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">What happened?</Label>
                    <Textarea data-testid="scanner-case-description" value={caseForm.description}
                      onChange={(e) => setCaseForm({ ...caseForm, description: e.target.value })} rows={3}
                      placeholder="Describe the cheating shown in this clip." className="mt-1.5 border-[#1E293F] bg-[#0F1424] text-white placeholder:text-[#64748B]" />
                  </div>
                  <Button data-testid="scanner-create-case-btn" onClick={createCase} disabled={creating}
                    className="w-full bg-[#F97316] font-bold text-white hover:bg-[#F97316]/90">
                    {creating ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <FilePlus size={15} className="mr-1.5" />} Create case &amp; send to queue
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
